import { CURRICULUM_KEYS, type TrieKey } from '@mathflat/domain/Curriculum'
import {
  type AllGradeSubject,
  type GradeElementaryMiddle,
  type SchoolType,
  SchoolTypeSchema,
  type Semester,
} from '@mathflat/domain/SchoolSystem'
import type { SignatureWorkbook, WorkbookFeatureType } from '@mathflat/domain/Workbook'
import { SIGNATURE_TYPE } from '@mathflat/domain/Workbook'
import { tJosa } from '@mathflat/shared/i18n/tJosa'

import type { Academy } from '@/dto'
import { RouteName } from '@/router'
import { curriculumCoreService } from '@/service/curriculumCore.service'

type ValidateSubscriptionParamType =
  | {
      schoolType: SchoolType | null
      grade?: AllGradeSubject | null
    }
  | { trieKey: TrieKey | null }

export class SubscriptionPermission {
  private _academyFeatureAuthority: Academy['authority']
  private _subscribeItems: Academy['subscribeItems']
  private _payStatus: Academy['payStatus']
  private _isSeoltab: boolean

  constructor({
    academyFeatureAuthority,
    subscribeItems,
    payStatus,
    isSeoltab,
  }: {
    academyFeatureAuthority: Academy['authority']
    subscribeItems: Academy['subscribeItems']
    payStatus: Academy['payStatus']
    isSeoltab: boolean
  }) {
    this._academyFeatureAuthority = academyFeatureAuthority
    this._payStatus = payStatus
    this._subscribeItems = subscribeItems
    this._isSeoltab = isSeoltab
  }

  get isSolo() {
    return this._academyFeatureAuthority === 'SOLO'
  }

  get 솔로_구독_커리큘럼() {
    if (!this.isSolo) return null
    if (this._subscribeItems?.length === 0) return null
    // 서버는 외부에서 적힌 JSON 을 그대로 풀어 내므로 학교급을 알 수 없는 항목은 버리고, 교육과정이 없으면 빈 목록으로 둔다
    return (
      this._subscribeItems?.flatMap((v) => {
        const schoolType = SchoolTypeSchema.safeParse(v.schoolType)
        if (!schoolType.success) return []
        return [
          {
            schoolType: schoolType.data,
            grade: `${v.grade}` as GradeElementaryMiddle,
            trieKeys: (v.trieKeys ?? []) as TrieKey[],
          },
        ]
      }) ?? []
    )
  }
  get workbook(): {
    signature: {
      featureTypesParams: WorkbookFeatureType[]
      enableRentWorkbook: boolean
      checkEnableDotDotDotMenu: (featureType: WorkbookFeatureType) => boolean
      enableAssignSignatureTypesParams: SignatureWorkbook['signatureType'][]
    }
  } {
    return {
      signature: {
        featureTypesParams:
          this._academyFeatureAuthority === 'PREMIUM'
            ? ['SAMPLE', 'OWN', 'RENT']
            : ['SAMPLE', 'OWN'],
        enableRentWorkbook: this._academyFeatureAuthority === 'PREMIUM',
        checkEnableDotDotDotMenu: (featureType) =>
          this._academyFeatureAuthority === 'PREMIUM' ||
          featureType === 'SAMPLE' ||
          this._payStatus !== '무료체험',
        enableAssignSignatureTypesParams: [...SIGNATURE_TYPE].filter(
          (signatureType) => signatureType !== 'ARITHMETIC',
        ),
      },
    }
  }

  validateSubscription(params: ValidateSubscriptionParamType) {
    if (!this.isSolo) return true

    if ('trieKey' in params) {
      if (!params.trieKey) return true
      return this._isSubscribedByTrieKey(params.trieKey)
    } else if (params.schoolType && params.grade) {
      const schoolType = params.schoolType
      const grade = params.grade
      switch (schoolType) {
        case 'ELEMENTARY':
        case 'MIDDLE':
        default:
          return this._isSubscribedByGrade(schoolType, grade)
        case 'HIGH': {
          if (Number.isInteger(grade)) {
            return this._isSubscribedByGrade(schoolType, grade)
          }
          const targetCurriculumKey = curriculumCoreService.getItemByGradeSemesterInfo({
            schoolType,
            gradeSubject: grade!,
            semester: null,
          })?.trieKey

          return targetCurriculumKey ? this._isSubscribedByTrieKey(targetCurriculumKey) : false
        }
      }
    } else if (params.schoolType) {
      return this._isSubscribedBySchoolType(params.schoolType)
    } else {
      return true
    }
  }

  private _isSubscribedBySchoolType(schoolType: SchoolType): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }
    return this.솔로_구독_커리큘럼.some((sub) => sub.schoolType === schoolType)
  }

  private _isSubscribedByGrade(schoolType: SchoolType, grade: AllGradeSubject | null): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }
    return this.솔로_구독_커리큘럼.some(
      (sub) => sub.schoolType === schoolType && sub.grade === grade,
    )
  }

  private _isSubscribedByTrieKey(trieKey: TrieKey): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }
    return this.솔로_구독_커리큘럼.some((sub) => sub.trieKeys.includes(trieKey))
  }

  isExamOrCategoryWorksheetSubscribed(routeName: ValueOf<typeof RouteName>): boolean {
    if (!this.isSolo || !this.솔로_구독_커리큘럼) {
      return true
    }

    const info = EXAM_AND_CATEGORY_WORKSHEET_SUBSCRIPTION_MAPPER[routeName]

    const checkIsSubscribed = (
      subscriptionList: CurriculumInfo[],
      userSubscription: {
        schoolType: SchoolType
        grade: AllGradeSubject | null
        trieKeys: string[]
      },
    ) =>
      subscriptionList.some(({ schoolType, grade, trieKeys }) => {
        if (trieKeys) {
          return userSubscription.trieKeys.every((key) => trieKeys.includes(key))
        }
        if (grade) {
          return userSubscription.schoolType === schoolType && userSubscription.grade === grade
        }
        return userSubscription.schoolType === schoolType
      })

    if (info.blackList.length !== 0) {
      // 구독하고 있는 모든 학제/학년 중 하나라도 블랙리스트 조건에 해당하지 않는다면 구독 상태
      return this.솔로_구독_커리큘럼.some(
        (subscribe) => !checkIsSubscribed(info.blackList, subscribe),
      )
    }

    if (info.whiteList.length !== 0) {
      // 구독하고 있는 모든 학제/학년 중 하나라도 화이트리스트 조건에 해당한다면 구독 상태
      return this.솔로_구독_커리큘럼.some((subscribe) =>
        checkIsSubscribed(info.whiteList, subscribe),
      )
    }

    return true
  }

  get firstSubscriptedSchoolType() {
    return this.솔로_구독_커리큘럼?.[0]?.schoolType ?? 'MIDDLE'
  }

  get firstSubscriptionGrade() {
    return this.firstSubscriptionBySchoolType[this.firstSubscriptedSchoolType]?.grade ?? undefined
  }

  get firstSubscriptionTrieKeys() {
    return this.firstSubscriptionBySchoolType[this.firstSubscriptedSchoolType]?.trieKeys ?? []
  }

  get everyTrieKeys() {
    return this.솔로_구독_커리큘럼?.map((sub) => sub.trieKeys).flat() ?? []
  }

  get everySemesterSubjectIds() {
    return this.everyTrieKeys
      .map((key) => key.split('.')[key.split('.').length - 1])
      .filter((str) => str !== undefined)
  }

  private get firstSubscriptionBySchoolType() {
    const checkFirstSubscriptionBySchoolType = (schoolType: SchoolType) => {
      return this.솔로_구독_커리큘럼?.find((sub) => sub.schoolType === schoolType)
    }

    return {
      HIGH: checkFirstSubscriptionBySchoolType('HIGH'),
      MIDDLE: checkFirstSubscriptionBySchoolType('MIDDLE'),
      ELEMENTARY: checkFirstSubscriptionBySchoolType('ELEMENTARY'),
    }
  }

  validateContentSubscription({
    contentSchoolType,
    contentGrade,
  }: {
    contentSchoolType: SchoolType
    contentGrade: AllGradeSubject
  }): boolean {
    if (!this.isSolo) return true
    if (!this.솔로_구독_커리큘럼) return false

    const item = curriculumCoreService.getItemByGradeSemesterInfo({
      schoolType: contentSchoolType,
      gradeSubject: contentGrade,
      semester: null,
    })
    return item?.subscribeStatus === 'SUBSCRIBED'
  }
}

type CurriculumInfo =
  | {
      schoolType: SchoolType
      grade?: AllGradeSubject | null
      semester?: NonNullable<Semester>
      trieKeys?: never
    }
  | { trieKeys: TrieKey[]; schoolType?: never; grade?: never; semester?: never }

export type SubscriptionCurriculum = {
  isUnusedDefaultDescription?: boolean
  labelType: ('GRADE_SUBJECT' | 'GRADE_ONLY')[]
  descriptions: string[]
  whiteList: CurriculumInfo[]
  blackList: CurriculumInfo[]
  excludeRevision22?: boolean
}

export const getLessonPrepareSubscriptionMapper = (): Record<string, SubscriptionCurriculum> => ({
  [RouteName.lecture]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: [`매쓰플랫에서 제공하는 ${tJosa('강의', '을/를')} 시청할 수 있어요.`],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
        grade: '1',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '2',
      },
    ],
  },
})

export const SIGNATURE_WORKBOOK_SUBSCRIPTION = [
  '초등학교 1학년, 2학년 시그니처 교재는 콘텐츠 준비중입니다.',
  '조금만 기다려주시면 양질의 교재로 보답해드릴게요!',
]

export const getMakeWorksheetSubscriptionMapper = (): Record<string, SubscriptionCurriculum> => ({
  [RouteName.signatureWorkbook]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: ['매쓰플랫에서 엄선한 문제로 구성된 시그니처 교재를 사용할 수 있어요.'],
    whiteList: [],
    blackList: [
      // 커리큘럼 key로 바꿀 것인가?
      {
        schoolType: 'ELEMENTARY',
        grade: '1',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '2',
      },
    ],
  },
  [RouteName.makeWorksheetByPracticeTest]: {
    labelType: ['GRADE_ONLY', 'GRADE_SUBJECT'],
    descriptions: ['고1~고3 수능/모의고사 문제로 학습지를 만들 수 있어요.'],
    whiteList: [
      {
        schoolType: 'HIGH',
      },
    ],
    blackList: [],
    excludeRevision22: true,
  },
  [RouteName.makeWorksheetBySchoolTest]: {
    labelType: ['GRADE_ONLY'],
    descriptions: [`학교별 기출 문제의 쌍둥이・유사문제를 ${tJosa('출제')}할 수 있어요.`],
    whiteList: [
      {
        schoolType: 'MIDDLE',
      },
      {
        schoolType: 'HIGH',
      },
    ],
    blackList: [],
  },
  [RouteName.editWorksheetProblems]: {
    labelType: [],
    isUnusedDefaultDescription: true,
    descriptions: [
      '수능/모의고사 학습지의 쌍둥이・유사 기능을 사용하려면',
      '요금제 업그레이드를 진행해주세요.',
    ],
    whiteList: [],
    blackList: [],
  },
})

export const EXAM_AND_CATEGORY_WORKSHEET_SUBSCRIPTION_MAPPER: Record<
  string,
  SubscriptionCurriculum
> = {
  [RouteName.textbookWorksheet]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: ['교과서별 단원 정리에 맞춘 쌍둥이 문제로 구성된 학습지를 사용하실 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
    ],
  },
  [RouteName.recommend]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: ['중간, 기말고사 내신 대비를 위한 추천 학습지를 사용하실 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
    ],
  },
  [RouteName.schoolTest]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: ['학교별 기출 시험지와 내신 대비를 위한 추천 학습지 세트를 사용하실 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
    ],
  },
  [RouteName.practiceTest]: {
    labelType: ['GRADE_ONLY', 'GRADE_SUBJECT'],
    descriptions: ['전국 교육청 별 모의고사 시험지를 사용할 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
      {
        schoolType: 'MIDDLE',
      },
    ],
    excludeRevision22: true,
  },
  [RouteName.practiceTestPair]: {
    labelType: ['GRADE_ONLY', 'GRADE_SUBJECT'],
    descriptions: ['전국 교육청 별 모의고사의 쌍둥이 시험지를 사용하실 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
      {
        schoolType: 'MIDDLE',
      },
    ],
    excludeRevision22: true,
  },
  [RouteName.satWorksheet]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: ['수능특강과 수능완성의 변형 문제로 구성된 학습지를 사용하실 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
      {
        schoolType: 'MIDDLE',
      },
      {
        schoolType: 'HIGH',
        grade: '1',
      },
      {
        trieKeys: [
          CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하,
          CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정,
        ],
      },
    ],
    excludeRevision22: true,
  },
  [RouteName.kmmExamWorksheet]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: [
      '매쓰플랫 고객 전체를 대상으로 전국 기준 응시 학생의 위치와 성취도 추이를',
      '알 수 있는 평가 서비스입니다. 매월 새로운 회차의 시험이 제공됩니다.',
    ],
    blackList: [
      {
        schoolType: 'HIGH',
      },
    ],
    whiteList: [],
  },
  [RouteName.policeMilitaryPracticeTest]: {
    labelType: ['GRADE_ONLY', 'GRADE_SUBJECT'],
    descriptions: ['경찰대·사관학교의 기출 문제를 활용하실 수 있어요.'],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
      {
        schoolType: 'MIDDLE',
      },
    ],
    whiteList: [],
    excludeRevision22: true,
  },
  [RouteName.policeMilitaryPracticeTestPair]: {
    labelType: ['GRADE_ONLY', 'GRADE_SUBJECT'],
    descriptions: ['경찰대·사관학교 기출의 쌍둥이 문제를 활용하실 수 있어요.'],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
      },
      {
        schoolType: 'MIDDLE',
      },
    ],
    whiteList: [],
    excludeRevision22: true,
  },
  [RouteName.themeEntranceTest]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: [
      '수준별 반 편성을 위해 학생의 개인별 학습 능력을 측정, 확인하는 테스트를 사용하실 수 있어요.',
    ],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
        grade: '1',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '2',
      },
      {
        schoolType: 'HIGH',
        grade: '2',
      },
      {
        schoolType: 'HIGH',
        grade: '3',
      },
    ],
  },
  [RouteName.weeklyTest]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: [
      '학습 선행진도와 별도로 학생의 학년 진도에 맟춘 주간 단위 테스트를 사용하실 수 있어요.',
    ],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
        grade: '1',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '2',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '3',
      },
      {
        schoolType: 'HIGH',
      },
    ],
  },
  [RouteName.chapterTest]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: [
      '학생이 대단원에서 학습한 내용을 얼마나 이해하였는지 알아보는 테스트를 사용하실 수 있어요.',
    ],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
        grade: '1',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '2',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '3',
      },
      {
        schoolType: 'HIGH',
        grade: '2',
      },
      {
        schoolType: 'HIGH',
        grade: '3',
      },
    ],
  },
  [RouteName.generalTest]: {
    labelType: ['GRADE_SUBJECT'],
    descriptions: ['한 학기동안 배운 내용을 총정리한 최종 실력 점검 테스트를 사용하실 수 있어요.'],
    whiteList: [],
    blackList: [
      {
        schoolType: 'ELEMENTARY',
        grade: '1',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '2',
      },
      {
        schoolType: 'ELEMENTARY',
        grade: '3',
      },
      {
        schoolType: 'HIGH',
        grade: '2',
      },
      {
        schoolType: 'HIGH',
        grade: '3',
      },
    ],
  },
}
